import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toPaise, sumPaise } from '@vns/core';
import { supabase } from './supabase';
import { useSession } from './session';

export interface ExpenseLine {
  category_id: string;
  custom_category?: string;
  title: string;
  amountRupees: string;
  paid_to: string;
  remarks: string;
}

export const emptyExpenseLine = (): ExpenseLine => ({
  category_id: '',
  custom_category: '',
  title: '',
  amountRupees: '',
  paid_to: '',
  remarks: '',
});

export interface ExpenseDraft {
  date: string;
  bill_no: string;
  default_paid_to: string;
  remarks: string;
  lines: ExpenseLine[];
}

export function expenseLineFilled(l: ExpenseLine): boolean {
  const amt = l.amountRupees.trim().replace(/[₹,\s]/g, '');
  return (
    (!!l.title.trim() || !!l.category_id) &&
    !!amt &&
    Number.isFinite(Number(amt)) &&
    Number(amt) > 0
  );
}

export function expenseLineTotalPaise(l: ExpenseLine): number | null {
  const amt = l.amountRupees.trim().replace(/[₹,\s]/g, '');
  if (!amt || !Number.isFinite(Number(amt)) || Number(amt) <= 0) return null;
  try {
    return toPaise(amt);
  } catch {
    return null;
  }
}

export function expensesTotalPaise(lines: ExpenseLine[]): number {
  return sumPaise(lines.map((l) => expenseLineTotalPaise(l) ?? 0));
}

export function filledExpenseCount(lines: ExpenseLine[]): number {
  return lines.filter(expenseLineFilled).length;
}

/* ------------------------------ validation ------------------------------ */

export interface ExpenseErrors {
  date?: string;
  lines: Record<number, Partial<Record<keyof ExpenseLine, string>>>;
  form?: string;
}

export function validateExpense(draft: ExpenseDraft): ExpenseErrors {
  const e: ExpenseErrors = { lines: {} };
  if (!draft.date) e.date = 'Enter the date';

  const touchedRows = draft.lines.filter(
    (l) => l.category_id || l.title.trim() || l.amountRupees.trim(),
  );
  if (!touchedRows.length) e.form = 'Add at least one expense';

  draft.lines.forEach((l, i) => {
    const isTouched = l.category_id || l.title.trim() || l.amountRupees.trim();
    if (!isTouched) return; // empty spare row

    const le: Partial<Record<keyof ExpenseLine, string>> = {};

    if (!l.title.trim()) le.title = 'Enter expense description';

    const amt = l.amountRupees.trim().replace(/[₹,\s]/g, '');
    if (!amt) le.amountRupees = 'Enter an amount';
    else {
      const num = Number(amt);
      if (!Number.isFinite(num) || num <= 0) le.amountRupees = 'Enter a valid amount';
      else {
        try {
          toPaise(amt);
        } catch {
          le.amountRupees = 'Invalid amount';
        }
      }
    }

    if (Object.keys(le).length) e.lines[i] = le;
  });

  return e;
}

export function expenseHasErrors(e: ExpenseErrors): boolean {
  return !!(e.date || e.form || Object.keys(e.lines).length);
}

/* -------------------------------- writing -------------------------------- */

export function useCreateExpenseBatch(projectId: string) {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async (draft: ExpenseDraft): Promise<void> => {
      if (!actor) throw new Error('Not signed in');
      const errors = validateExpense(draft);
      if (expenseHasErrors(errors)) throw new Error(errors.form ?? 'Check the highlighted fields');

      const filled = draft.lines.filter(expenseLineFilled);
      if (!filled.length) throw new Error('Add at least one expense');

      const rows = filled.map((l) => {
        const isOther = l.category_id === '__other__';
        const categoryId = isOther ? null : (l.category_id || null);
        const customNote = isOther && l.custom_category?.trim() ? `Category: ${l.custom_category.trim()}` : '';
        const notes = [customNote, l.remarks?.trim(), draft.remarks?.trim()].filter(Boolean).join(' | ');

        return {
          company_id: actor.companyId,
          project_id: projectId,
          category_id: categoryId,
          spent_on: draft.date,
          title: l.title.trim(),
          amount_paise: toPaise(l.amountRupees.replace(/[₹,\s]/g, '')),
          paid_to: (l.paid_to?.trim() || draft.default_paid_to?.trim()) || null,
          bill_no: draft.bill_no?.trim() || null,
          remarks: notes || null,
        };
      });

      const { error } = await supabase.from('expenses').insert(rows);
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['project'] }),
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['expenses'] }),
        qc.invalidateQueries({ queryKey: ['expense-transactions'] }),
        qc.invalidateQueries({ queryKey: ['approvals'] }),
        qc.invalidateQueries({ queryKey: ['my-submissions'] }),
        qc.invalidateQueries({ queryKey: ['staff-dashboard'] }),
        qc.invalidateQueries({ queryKey: ['company-metrics'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
  });
}
